import { useEffect, useRef, useState } from "react";

const POINTER_EVENTS = [
  "mousemove",
  "mousedown",
  "mouseup",
  "pointermove",
  "pointerdown",
  "pointerup",
  "touchmove",
  "touchstart",
  "touchend",
];

/**
 * Ported to React from https://github.com/jonathantneal/keyboard-focus
 */
export function useIsKeyboardFocus<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [hadKeyboardEvent, setHadKeyboardEvent] = useState(true);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  const [triggerInitialPointerMoveListeners, setTriggerInitialPointerMove] =
    useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const onInitialPointerMove = () => {
      setHadKeyboardEvent(false);
      // remove all initial pointer move listeners
      controller.abort();
    };

    for (const event in POINTER_EVENTS) {
      document.addEventListener(event, onInitialPointerMove, {
        once: true,
        passive: true,
        capture: true,
        signal: controller.signal,
      });
    }

    return () => {
      controller.abort();
    };
  }, [triggerInitialPointerMoveListeners]);

  useEffect(() => {
    const controller = new AbortController();

    const options: AddEventListenerOptions = {
      passive: true,
      capture: true,
      signal: controller.signal,
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      setHadKeyboardEvent(true);
    };

    const onPointerDown = () => {
      setHadKeyboardEvent(false);
    };

    const onFocus = (event: FocusEvent) => {
      if (event.target === ref.current && hadKeyboardEvent) {
        setHadKeyboardEvent(false);
        setHasKeyboardFocus(true);
      }
    };

    const onBlur = (event: FocusEvent) => {
      if (
        event.target === document ||
        (event.target as Node)?.nodeName === "HTML"
      ) {
        return;
      }

      setHasKeyboardFocus(false);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (hasKeyboardFocus) {
          setHadKeyboardEvent(true);
        }
      }

      setTriggerInitialPointerMove((prev) => prev + 1);
    };

    document.addEventListener("keydown", onKeyDown, options);
    document.addEventListener("pointerdown", onPointerDown, options);
    document.addEventListener("mousedown", onPointerDown, options);
    document.addEventListener("touchstart", onPointerDown, options);
    document.addEventListener("focus", onFocus, options);
    document.addEventListener("blur", onBlur, options);
    document.addEventListener("visibilitychange", onVisibilityChange, options);

    return () => {
      controller.abort();
    };
  }, [hadKeyboardEvent, hasKeyboardFocus]);

  return [ref, hasKeyboardFocus] as const;
}
